import {
    ClaimApi,
    ClaimGuarantorResponsibility,
    ClaimType, CoreFetch,
    createRequestFilters,
    CurrencyType,
    FilterMethod,
    formatDate,
    ICustomFilter,
    Invoice, ListProviderClaimsFilterClaimTypeEnum,
    ListProviderClaimsOrderEnum,
    ListProviderClaimsRequest,
    ListProviderLettersRequest,
    ListProviderMailCartsRequest,
    ListProviderMailRequest, ListProviderPhoneRequest,
    ListProviderReceiveFaxesRequest,
    ListProviderSendFaxesRequest,
    PaymentPlanIntervalType,
    ProviderChargemasterItem,
    ProviderClaim,
    ProviderClaimEligibility,
    ProviderClaimPayment,
    ProviderClaimSortType,
    ProviderClaimTransmission,
    ProviderCollectionPayment,
    ProviderCreateCollectionPaymentPayload,
    ProviderDisplayDocumentType,
    ProviderDocument, ProviderDocumentTranscription,
    ProviderDocumentUrl,
    ProviderFacility,
    ProviderGuarantor,
    ProviderInstitutionalClaim,
    ProviderInstitutionalClaimTemplate,
    ProviderLetter,
    ProviderMail,
    ProviderMailCart,
    ProviderMedicalClaim,
    ProviderNynf3Claim,
    ProviderNynf3ClaimTemplate,
    ProviderPatient,
    ProviderPayer,
    ProviderPayerSubscriber,
    ProviderPaymentPlan, ProviderPhone,
    ProviderProfessionalClaim,
    ProviderProfessionalClaimTemplate,
    ProviderProvider,
    ProviderReceiveFax,
    ProviderSeller, ProviderSendEmail,
    ProviderSendFax,
    ProviderStatus,
    ProviderSubscriber,
    ProviderSubscriberPatient, SupportChargemasterItem,
    SupportCheckoutCharge,
    SupportCreditCard,
    SupportHL7Message, SupportMedicalClaimResponse,
    SupportRefundCollectionPaymentPayload,
    SupportShortUrl,
    TemporaryDocumentUrl,
    Transaction,
} from '@enter/core';
import _ from 'lodash';
import { action, observable, runInAction } from 'mobx';
import { ClaimGenerator } from './claim.generator';
import { ClaimService } from './claim.service';
import {
    ICommunication,
    IFeatures,
    IItemListing,
    IItemListingRequest,
    ListProviderCommunicationsRequest,
} from './root.store';
import { appendTo, buildCacheKey, icf2lpcr, parseItemListingRequest, updateClaimStatus } from './util';

export interface IRecord<T> {
    data: T;
    loaded: boolean;
    // Change `loaded` to `status` if error status is required
}

function createRecord<T>(data: T, loaded = false): IRecord<T> {
    return {
        data,
        loaded,
    };
}

export enum LocalClaimSortType {
    PAYER = 'PAYER', // FIXME: replace with ProviderClaimSortType when ready
    STATUS = 'STATUS', // FIXME: replace with ProviderClaimSortType when ready
    TYPE = 'TYPE',
    PATIENT_PAID = 'PATIENT_PAID',
    FACTORED = 'FACTORED',
    FACTOR_RECOVERED = 'FACTOR_RECOVERED',
}
export type ClaimSortType = ProviderClaimSortType | LocalClaimSortType;
export type ClaimSortOrder = ListProviderClaimsOrderEnum;

export interface IClaimDetail {
    claim?: ProviderClaim;
    chargemasterItems?: ProviderChargemasterItem[];
    checkoutCharges?: SupportCheckoutCharge[];
    collections?: ProviderCollectionPayment[];
    creditCards?: SupportCreditCard[];
    documents?: ProviderDocument[];
    eligibilities?: ProviderClaimEligibility[];
    emails?: ProviderSendEmail[];
    facility?: ProviderFacility;
    guarantor?: ProviderGuarantor;
    hl7Messages?: SupportHL7Message[];
    institutionalClaim?: ProviderInstitutionalClaim;
    institutionalTemplate?: ProviderInstitutionalClaimTemplate;
    invoices?: Invoice[];
    letters?: ProviderLetter[];
    mails?: ProviderMail[];
    nynf3Claim?: ProviderNynf3Claim;
    nynf3Template?: ProviderNynf3ClaimTemplate;
    patients?: ProviderPatient[];
    payers?: ProviderPayer[];
    payerSubscribers?: ProviderPayerSubscriber[];
    paymentPlans?: ProviderPaymentPlan[];
    payments?: ProviderClaimPayment[];
    phones? : ProviderPhone[];
    professionalClaim?: ProviderProfessionalClaim;
    professionalTemplate?: ProviderProfessionalClaimTemplate;
    providers?: ProviderProvider[];
    receiveFaxes?: ProviderReceiveFax[];
    seller?: ProviderSeller;
    sendFaxes?: ProviderSendFax[];
    statuses?: ProviderStatus[];
    subscribers?: ProviderSubscriber[];
    subscriberPatients?: ProviderSubscriberPatient[];
    transactions?: Transaction[];
    transmissions?: ProviderClaimTransmission[];
}

export interface IPayerListing {
    payers: ProviderPayer[];
    limit: number;
    offset: number;
    total: number;
}

export interface IClaimListRequest {
    startDate: Date;
    endDate: Date;
    page: number;
    pageSize: number;
    sortColumns: ClaimSortType[];
    sortOrders: ClaimSortOrder[];
    filters: ICustomFilter[];
}

export interface IClaimRequest {
    claimId: string;
    claimType: string;
}

export interface IClaimDetailRequest {
    claimId: string;
    claimType: string;
}

// Convert from IClaimListRequest to ListProviderClaimsRequest
export const clr2lpcr = (clr: IClaimListRequest): ListProviderClaimsRequest => {
    const isoDateFormat = formatDate('Date');
    const isLocalSort = (t: ClaimSortType): boolean => _.includes(_.values(LocalClaimSortType), t);
    const isServerSort = (t: ClaimSortType): boolean => _.includes(_.values(ProviderClaimSortType), t);

    // NB: if local sorting is occurring, use a larger limit size
    const hasLocalSort = _.some(clr.sortColumns, isLocalSort);
    const limit = Math.min(false && hasLocalSort ? clr.pageSize * 5 : clr.pageSize, 100);
    const offset = clr.page * limit;

    const sort = _.filter(clr.sortColumns, isServerSort) as ProviderClaimSortType[];
    const order = _.isEmpty(sort) ? [] : clr.sortOrders;
    const dateRangeFilter: ICustomFilter = {
        filterType: 'String',
        filterId: 'filterFromDos',
        filterMethod: FilterMethod.BT,
        value: `${isoDateFormat(clr.startDate)}:${isoDateFormat(clr.endDate)}`,
    };
    const filters: ListProviderClaimsRequest = icf2lpcr([dateRangeFilter, ...clr.filters]);
    return {
        limit,
        offset,
        order,
        sort,
        ...filters,
    };
};

export type ClaimDetailsKey =
    | 'facilities'
    | 'providers'
    | 'guarantors'
    | 'payerSubscribers'
    | 'sellers'
    | 'subscriberPatients'
    | 'chargemasterItems'
    | 'payments'
    | 'professionalTemplate'
    | 'institutionalTemplate'
    | 'nynf3Template'
    | 'payers'
    | 'subscribers'
    | 'eligibilities'
    | 'patients'
    | 'creditCards'
    | 'checkoutCharges'
    | 'paymentPlans'
    | 'transactions'
    | 'invoices'
    | 'professionalClaims'
    | 'institutionalClaims'
    | 'phones'
    | 'nynf3Claims';

export class ClaimStore {
    // used by pages
    @observable public claimDetails: IClaimDetail[] = [];
    @observable.deep public claimDetailsLoader: Map<ClaimDetailsKey, boolean> = new Map();

    // claims related
    @observable public chargemasterItems: ProviderChargemasterItem[] = [];
    @observable public checkoutCharges: SupportCheckoutCharge[] = [];
    @observable public claims: ProviderClaim[] = [];
    @observable public collections: ProviderCollectionPayment[] = [];
    @observable public creditCards: SupportCreditCard[] = [];
    @observable public documents: ProviderDocument[] = [];
    @observable public documentUrls: ProviderDocumentUrl[] = [];
    @observable public eligibilities: ProviderClaimEligibility[] = [];
    @observable public facilities: ProviderFacility[] = [];
    @observable public guarantors: ProviderGuarantor[] = [];
    @observable public hl7Messages: SupportHL7Message[] = [];
    @observable public invoices: Invoice[] = [];
    @observable public institutionalClaims: ProviderInstitutionalClaim[] = [];
    @observable public institutionalTemplates: ProviderInstitutionalClaimTemplate[] = [];
    @observable public letters: ProviderLetter[] = [];
    @observable public mails: ProviderMail[] = [];
    @observable public mailCarts: ProviderMailCart[] = [];
    @observable public nynf3Claims: ProviderNynf3Claim[] = [];
    @observable public nynf3Templates: ProviderNynf3ClaimTemplate[] = [];
    @observable public patients: ProviderPatient[] = [];
    @observable public payers: ProviderPayer[] = [];
    @observable public emails: ProviderSendEmail[] = [];
    @observable public phones: ProviderPhone[] = [];
    @observable public payerSubscribers: ProviderPayerSubscriber[] = [];
    @observable public paymentPlans: ProviderPaymentPlan[] = [];
    @observable public payments: ProviderClaimPayment[] = [];
    @observable public professionalClaims: ProviderProfessionalClaim[] = [];
    @observable public professionalTemplates: ProviderProfessionalClaimTemplate[] = [];
    @observable public providers: ProviderProvider[] = [];
    @observable public receiveFaxes: ProviderReceiveFax[] = [];
    @observable public sellers: ProviderSeller[] = [];
    @observable public sendFaxes: ProviderSendFax[] = [];
    @observable public statuses: ProviderStatus[] = [];
    @observable public subscribers: ProviderSubscriber[] = [];
    @observable public subscriberPatients: ProviderSubscriberPatient[] = [];
    @observable public transactions: Transaction[] = [];
    @observable public transmissions: ProviderClaimTransmission[] = [];

    @observable public communications: ICommunication[] = [];

    // NB: client side memory-only caches
    // TODO: need space savings for model arrays which may contain duplicates
    //       consider mapping queries to arrays of IDs, instead
    // TODO: add timing information instead of caching forever

    // NB: query maps are used to cache results of individual queries
    @observable public claimQueries: Map<string, ProviderClaim> = new Map();
    @observable public claimListingQueries: Map<string, IItemListing<ProviderClaim>> = new Map();
    @observable public claimDetailQueries: Map<string, IClaimDetail> = new Map();
    @observable public payerQueries: Map<string, ProviderPayer[]> = new Map();
    @observable public providerQueries: Map<string, ProviderProvider[]> = new Map();
    @observable public documentQueries: Map<string, ProviderDocument> = new Map();
    @observable public documentUrlQueries: Map<string, ProviderDocumentUrl> = new Map();
    @observable public mailCartQueries: Map<string, ProviderMailCart> = new Map();

    // NB: indices are used to allow searching by 'claimId'
    @observable public chargemasterItemIndex: Map<string, string[]> = new Map<string, string[]>();
    @observable public eligibilityIndex: Map<string, string[]> = new Map<string, string[]>();
    @observable public facilityIndex: Map<string, string[]> = new Map<string, string[]>();
    @observable public guarantorIndex: Map<string, string[]> = new Map<string, string[]>();
    @observable public hl7MessageIndex: Map<string, string[]> = new Map<string, string[]>();
    @observable public invoiceIndex: Map<string, string[]> = new Map<string, string[]>();
    @observable public letterIndex: Map<string, string[]> = new Map<string, string[]>();
    @observable public mailIndex: Map<string, string[]> = new Map<string, string[]>();
    @observable public phoneIndex: Map<string, string[]> = new Map<string, string[]>();
    @observable public providerIndex: Map<string, string[]> = new Map<string, string[]>();
    @observable public receiveFaxIndex: Map<string, string[]> = new Map<string, string[]>();
    @observable public sellerIndex: Map<string, string[]> = new Map<string, string[]>();
    @observable public sendFaxIndex: Map<string, string[]> = new Map<string, string[]>();
    @observable public transactionIndex: Map<string, string[]> = new Map<string, string[]>();
    @observable public payerSubscriberIndex: Map<string, string[]> = new Map<string, string[]>();
    @observable public subscriberPatientIndex: Map<string, string[]> = new Map<string, string[]>();
    @observable public transmissionIndex: Map<string, string[]> = new Map<string, string[]>();
    @observable public emailIndex: Map<string, string[]> = new Map<string, string[]>();
    @observable public templateIndex: Map<string, string[]> = new Map<string, string[]>();

    // NB: index checkoutmodels by guarantorId
    @observable public checkoutChargeIndex: Map<string, string[]> = new Map<string, string[]>();
    @observable public creditCardIndex: Map<string, string[]> = new Map<string, string[]>();

    private features: IFeatures;
    private claimService: ClaimService = new ClaimService();
    private claimGenerator: ClaimGenerator;

    constructor(features: IFeatures, claimGenerator: ClaimGenerator) {
        this.features = features;
        this.claimGenerator = claimGenerator;
    }

    @action
    public async loadClaimListing(ilr: IItemListingRequest): Promise<IItemListing<ProviderClaim>> {
        const lpcr = ilr2lpcr(ilr);
        const key = buildCacheKey(lpcr);
        let claimListing = this.claimListingQueries.get(key);
        if (!claimListing) {
            claimListing = await (this.features.isDemo
                ? this.claimGenerator.generateClaimListing(lpcr)
                : this.claimService.fetchClaimListing(lpcr));
            if (!claimListing) {
                throw new Error(`No claims available`);
            }
            this.claimListingQueries.set(key, claimListing);
        }
        if (this.features.isDev) {
            // tslint:disable: no-console
            console.group('loadClaimListing');
            console.table(_.entries(claimListing));
            console.groupEnd();
            // tslint:enable: no-console
        }
        const { items } = claimListing;
        runInAction(() => {
            // NB: update all claims to ADJUDICATED if non-payer amounts exist.
            _.forEach(items, claim => updateClaimStatus(claim));
            this.claims = appendTo<ProviderClaim>(this.claims, items);
        });
        return claimListing;
    }

    @action public async loadClaim(claimId: string): Promise<ProviderClaim | undefined> {
        const key = buildCacheKey({ claimId });
        let claim = this.claimQueries.get(key);
        if (!claim) {
            claim = await (this.features.isDemo
                ? this.claimGenerator.generateClaim(claimId)
                : this.claimService.fetchClaim(claimId));
            this.claimQueries.set(key, claim as ProviderClaim);
        }
        runInAction(() => {
            if (claim) {
                this.claims = appendTo<ProviderClaim>(this.claims, updateClaimStatus(claim));
            }
        });
        return claim;
    }

    private async generateClaimDetail(claim: ProviderClaim) {
        const claimDetail = this.claimGenerator.generateClaimDetail(claim);

        return claimDetail;
    }


    @action
    public async loadProviderClaimWithSuperFastClaimDetails (claimId: string): Promise<IClaimDetail | undefined> {
        const superFastDetails = await CoreFetch<ClaimApi>(ClaimApi).getProviderClaimDetails({
            id: claimId
        });

        /*
        claim?: ProviderClaim;
    chargemasterItems?: ProviderChargemasterItem[];
    checkoutCharges?: SupportCheckoutCharge[];
    collections?: ProviderCollectionPayment[];
    creditCards?: SupportCreditCard[];
    documents?: ProviderDocument[];
    eligibilities?: ProviderClaimEligibility[];
    facility?: ProviderFacility;
    guarantor?: ProviderGuarantor;
    hl7Messages?: SupportHL7Message[];
    institutionalClaim?: ProviderInstitutionalClaim;
    institutionalTemplate?: ProviderInstitutionalClaimTemplate;
    invoices?: Invoice[];
    letters?: ProviderLetter[];
    mails?: ProviderMail[];
    nynf3Claim?: ProviderNynf3Claim;
    nynf3Template?: ProviderNynf3ClaimTemplate;
    patients?: ProviderPatient[];
    payers?: ProviderPayer[];
    payerSubscribers?: ProviderPayerSubscriber[];
    paymentPlans?: ProviderPaymentPlan[];
    payments?: ProviderClaimPayment[];
    professionalClaim?: ProviderProfessionalClaim;
    professionalTemplate?: ProviderProfessionalClaimTemplate;
    providers?: ProviderProvider[];
    receiveFaxes?: ProviderReceiveFax[];
    seller?: ProviderSeller;
    sendFaxes?: ProviderSendFax[];
    statuses?: ProviderStatus[];
    subscribers?: ProviderSubscriber[];
    subscriberPatients?: ProviderSubscriberPatient[];
    transactions?: Transaction[];
    transmissions?: ProviderClaimTransmission[];
         */

        const claimDetail: IClaimDetail = {
            claim: superFastDetails.claim,
            chargemasterItems: [], //LOADED
            checkoutCharges: [], //deprecated
            collections: superFastDetails.collectionPayments,
            creditCards: [], //deprecated
            documents: (superFastDetails.documents) ? superFastDetails.documents.filter(d=>d.documentType !== 'PHONE') : [],
            eligibilities: superFastDetails.eligibilities,
            facility: undefined, //LOADED
            guarantor: undefined, //LOADED
            hl7Messages: superFastDetails.hl7s,
            institutionalClaim: superFastDetails.institutionals && superFastDetails.institutionals.length > 0 ? superFastDetails.institutionals[0] : undefined,
            institutionalTemplate: undefined, //LOADED
            invoices: superFastDetails.invoices,
            letters: superFastDetails.letters,
            mails: superFastDetails.mails,
            nynf3Claim: undefined, //deprecated
            nynf3Template: undefined, //deprecated
            patients: [], //LOADED
            payers: [], //LOADED
            payerSubscribers: [], //LOADED
            paymentPlans: [], //deprecated
            payments: superFastDetails.payments,
            phones: superFastDetails.phones,
            professionalClaim: superFastDetails.professionals && superFastDetails.professionals.length > 0 ? superFastDetails.professionals[0]: undefined,
            professionalTemplate: undefined, //LOADED
            providers: [], //LOADED
            receiveFaxes: superFastDetails.receiveFaxes,
            seller: undefined, //LOADED
            sendFaxes: superFastDetails.sendFaxes,
            statuses: superFastDetails.statuses,
            subscribers: [], //LOADED
            subscriberPatients: [], //LOADED
            transactions: [], //deprecated
            transmissions: superFastDetails.transmissions
        };

        let providerMedicalClaim: ProviderMedicalClaim | undefined;

        if(claimDetail.professionalClaim) {
            providerMedicalClaim = claimDetail.professionalClaim;
            claimDetail.professionalTemplate = claimDetail.professionalClaim.professionalTemplate;

            if(claimDetail.professionalTemplate) {
                const supportProfessionalChargemasterItems: SupportChargemasterItem[] = [];
                if(claimDetail.professionalTemplate.f24ServiceLine) {
                    for(const providerProfessionalTemplateServiceLine of claimDetail.professionalTemplate.f24ServiceLine) {
                        if(providerProfessionalTemplateServiceLine.chargemasterItem) {
                            supportProfessionalChargemasterItems.push(providerProfessionalTemplateServiceLine.chargemasterItem)
                        }
                    }
                }
                claimDetail.chargemasterItems = supportProfessionalChargemasterItems;
            }

        } else if(claimDetail.institutionalClaim) {
            providerMedicalClaim = claimDetail.institutionalClaim;
            claimDetail.institutionalTemplate = claimDetail.institutionalClaim.institutionalTemplate;

            if(claimDetail.institutionalTemplate) {
                const supportInstitutionalChargemasterItems: SupportChargemasterItem[] = [];
                if(claimDetail.institutionalTemplate.f42ClaimLines) {
                    for(const providerInstitutionalTemplateServiceLine of claimDetail.institutionalTemplate.f42ClaimLines) {
                        if(providerInstitutionalTemplateServiceLine.chargemasterItem) {
                            supportInstitutionalChargemasterItems.push(providerInstitutionalTemplateServiceLine.chargemasterItem)
                        }
                    }
                }
                claimDetail.chargemasterItems = supportInstitutionalChargemasterItems;
            }
        }

        if(providerMedicalClaim) {
            if(providerMedicalClaim.providers) {
                claimDetail.providers = providerMedicalClaim.providers
            }
            if(providerMedicalClaim.facility) {
                claimDetail.facility = providerMedicalClaim.facility;
            }
            if(providerMedicalClaim.seller) {
                claimDetail.seller = providerMedicalClaim.seller;
            }
            if(providerMedicalClaim.guarantor) {
                claimDetail.guarantor = providerMedicalClaim.guarantor;
            }
            if(providerMedicalClaim.providers) {
                claimDetail.providers = providerMedicalClaim.providers;
            }
            if(providerMedicalClaim.subscriberPatients) {
                claimDetail.subscriberPatients = providerMedicalClaim.subscriberPatients;
                const claimDetailSubscribers = [];
                const claimDetailPatients = [];
                for(const subscriberPatient of claimDetail.subscriberPatients) {
                    if(subscriberPatient.subscriber) {
                        claimDetailSubscribers.push(subscriberPatient.subscriber);
                    }
                    if(subscriberPatient.patient) {
                        claimDetailPatients.push(subscriberPatient.patient);
                    }
                }
                claimDetail.patients = claimDetailPatients;
                claimDetail.subscribers = claimDetailSubscribers;
            }
            if(providerMedicalClaim.payerSubscribers) {
                claimDetail.payerSubscribers = providerMedicalClaim.payerSubscribers;
                const claimDetailPayers= [];
                for(const payerSubscriber of claimDetail.payerSubscribers) {
                    if(payerSubscriber.payer) {
                        claimDetailPayers.push(payerSubscriber.payer);
                    }
                }
                claimDetail.payers = claimDetailPayers;
            }
        }


        runInAction(() => {
            this.claimDetails = appendTo<IClaimDetail>(this.claimDetails, claimDetail);
        });


        return claimDetail;
    }

    @action
    public async loadClaimDetail(claimId: string, legacy?: boolean): Promise<IClaimDetail | undefined> {
        let claim: ProviderClaim | undefined = undefined;
        let claimDetail: IClaimDetail | undefined = undefined;

        if(legacy || this.features.isDemo) {
            claim = this.findClaim(claimId);
            if (!claim) {
                throw new Error(`No claim for ID: ${claimId}`);
            }
            const key = buildCacheKey({claimId});
            claimDetail = this.claimDetailQueries.get(key);
            if (!claimDetail) {
                claimDetail = await (this.features.isDemo ? this.generateClaimDetail(claim) : this.fetchClaimDetail(claim));
                this.claimDetailQueries.set(key, claimDetail as IClaimDetail);
            }

            if (!claimDetail) {
                throw new Error(`No claim detail for ID: ${claimId}`);
            }
        } else {
            claimDetail = await this.loadProviderClaimWithSuperFastClaimDetails(claimId);
            if(claimDetail) {
                claim = claimDetail.claim;

                /*
                asyncPopulateDetails
                 */
            } else {
                throw new Error(`No claim detail for ID: ${claimId}`);
            }
        }

        const {
            emails,
            chargemasterItems,
            checkoutCharges,
            collections,
            creditCards,
            documents,
            eligibilities,
            facility,
            guarantor,
            hl7Messages,
            institutionalClaim,
            institutionalTemplate,
            invoices,
            letters,
            mails,
            nynf3Claim,
            nynf3Template,
            patients,
            payers,
            payerSubscribers,
            paymentPlans,
            payments,
            phones,
            professionalClaim,
            professionalTemplate,
            providers,
            receiveFaxes,
            seller,
            sendFaxes,
            statuses,
            subscribers,
            subscriberPatients,
            transactions,
            transmissions,
        } = claimDetail;
        runInAction(() => {
            this.emails = appendTo<ProviderSendEmail>(this.emails, emails);
            this.chargemasterItems = appendTo<ProviderChargemasterItem>(this.chargemasterItems, chargemasterItems);
            this.checkoutCharges = appendTo<SupportCreditCard>(this.checkoutCharges, checkoutCharges);
            this.collections = appendTo<ProviderCollectionPayment>(this.collections, collections);
            this.creditCards = appendTo<SupportCreditCard>(this.creditCards, creditCards);
            this.documents = appendTo<ProviderDocument>(this.documents, documents);
            this.eligibilities = appendTo<ProviderClaimEligibility>(this.eligibilities, eligibilities);
            this.facilities = appendTo<ProviderFacility>(this.facilities, facility);
            this.guarantors = appendTo<ProviderGuarantor>(this.guarantors, guarantor);
            this.hl7Messages = appendTo<SupportHL7Message>(this.hl7Messages, hl7Messages);
            this.institutionalClaims = appendTo<ProviderInstitutionalClaim>(
                this.institutionalClaims,
                institutionalClaim,
            );
            this.institutionalTemplates = appendTo<ProviderInstitutionalClaimTemplate>(
                this.institutionalTemplates,
                institutionalTemplate,
            );
            this.invoices = appendTo<Invoice>(this.invoices, invoices);
            this.letters = appendTo<ProviderLetter>(this.letters, letters);
            this.mails = appendTo<ProviderMail>(this.mails, mails);
            this.nynf3Claims = appendTo<ProviderNynf3Claim>(this.nynf3Claims, nynf3Claim);
            this.nynf3Templates = appendTo<ProviderNynf3ClaimTemplate>(this.nynf3Templates, nynf3Template);
            this.patients = appendTo<ProviderPatient>(this.patients, patients);
            this.payers = appendTo<ProviderPayer>(this.payers, payers);
            this.payerSubscribers = appendTo<ProviderPayerSubscriber>(this.payerSubscribers, payerSubscribers);
            this.paymentPlans = appendTo<SupportCreditCard>(this.paymentPlans, paymentPlans);
            this.payments = appendTo<ProviderClaimPayment>(this.payments, payments);
            this.phones = appendTo<ProviderPhone>(this.phones, phones);
            this.professionalClaims = appendTo<ProviderProfessionalClaim>(this.professionalClaims, professionalClaim);
            this.professionalTemplates = appendTo<ProviderProfessionalClaimTemplate>(
                this.professionalTemplates,
                professionalTemplate,
            );
            this.providers = appendTo<ProviderProvider>(this.providers, providers);
            this.receiveFaxes = appendTo<ProviderReceiveFax>(this.receiveFaxes, receiveFaxes);
            this.sellers = appendTo<ProviderSendFax>(this.sellers, seller);
            this.sendFaxes = appendTo<ProviderSendFax>(this.sendFaxes, sendFaxes);
            this.statuses = appendTo<ProviderStatus>(this.statuses, statuses);
            this.subscribers = appendTo<ProviderSubscriber>(this.subscribers, subscribers);
            this.subscriberPatients = appendTo<ProviderSubscriberPatient>(this.subscriberPatients, subscriberPatients);
            this.transactions = appendTo<Transaction>(this.transactions, transactions);
            this.transmissions = appendTo<ProviderClaimTransmission>(this.transmissions, transmissions);


            // NB: update indices for models that are not searchable by claimId
            this.updateIndex(this.chargemasterItemIndex, claimId, chargemasterItems);
            this.updateIndex(this.eligibilityIndex, claimId, eligibilities);
            this.updateIndex(this.payerSubscriberIndex, claimId, payerSubscribers);
            this.updateIndex(this.subscriberPatientIndex, claimId, subscriberPatients);
            this.updateIndex(this.facilityIndex, claimId, _.compact([facility]));
            this.updateIndex(this.guarantorIndex, claimId, _.compact([guarantor]));
            this.updateIndex(this.hl7MessageIndex, claimId, hl7Messages); //
            this.updateIndex(this.invoiceIndex, claimId, invoices);
            this.updateIndex(this.letterIndex, claimId, letters); //
            this.updateIndex(this.mailIndex, claimId, mails); //
            this.updateIndex(this.providerIndex, claimId, providers);
            this.updateIndex(this.receiveFaxIndex, claimId, receiveFaxes); //
            this.updateIndex(this.sellerIndex, claimId, _.compact([seller]));
            this.updateIndex(this.sendFaxIndex, claimId, sendFaxes); //
            this.updateIndex(this.transactionIndex, claimId, transactions);
            this.updateIndex(this.transmissionIndex, claimId, transmissions); //
            this.updateIndex(this.emailIndex, claimId, emails);
            this.updateIndex(
                this.templateIndex,
                claimId,
                _.compact([institutionalTemplate, nynf3Template, professionalTemplate]),
            );
            this.updateIndex(this.phoneIndex, claimId, phones);

            const guarantorId = _.get(guarantor, 'id', '');
            this.updateIndex(this.checkoutChargeIndex, guarantorId, checkoutCharges); //
            this.updateIndex(this.creditCardIndex, guarantorId, creditCards); //

            // NB: useful debugging, need a better switch
            if (this.features.isDev) {
                // show some useful information
                const ok = (d: any): string => {
                    if (_.some(_.concat([], d), { id: 'id' })) {
                        // tslint:disable-next-line: no-console
                        console.warn('Found fixture in claim data!', d);
                        return '!';
                    }
                    return _.size(d) ? '⭕' : '❌';
                };
                const pind = _.compact([professionalClaim, institutionalClaim, nynf3Claim]);
                const pindTemplate = _.compact([professionalTemplate, institutionalTemplate, nynf3Template]);
                const report = [
                    // description, status,
                    ['Claim', _.get(claim, 'id'), ok(claim)],
                    ['PIND', _.size(pind), ok(pind)],
                    ['PIND Template', _.size(pindTemplate), ok(pindTemplate)],
                    ['Chargemaster Items', _.size(chargemasterItems), ok(chargemasterItems)],
                    ['Checkout Charges', _.size(checkoutCharges), ok(checkoutCharges)],
                    ['Collections Payments', _.size(collections), ok(collections)],
                    ['Credit Cards', _.size(creditCards), ok(creditCards)],
                    ['Documents', _.size(documents), ok(documents)],
                    ['Eligibilities', _.size(eligibilities), ok(eligibilities)],
                    ['Guarantor', _.size([guarantor]), ok([guarantor])],
                    ['HL7 Messages', _.size(hl7Messages), ok(hl7Messages)],
                    ['Invoices', _.size(invoices), ok(invoices)],
                    ['Letters', _.size(letters), ok(letters)],
                    ['Mails', _.size(mails), ok(mails)],
                    ['Patients', _.size(patients), ok(patients)],
                    ['Payers', _.size(payers), ok(payers)],
                    ['Payer Subscribers', _.size(payerSubscribers), ok(payerSubscribers)],
                    ['Payments', _.size(payments), ok(payments)],
                    ['Payment Plans', _.size(paymentPlans), ok(paymentPlans)],
                    ['Providers', _.size(providers), ok(providers)],
                    ['Receive Faxes', _.size(receiveFaxes), ok(receiveFaxes)],
                    ['Seller', _.size([seller]), ok([seller])],
                    ['Send Faxes', _.size(sendFaxes), ok(sendFaxes)],
                    ['Statuses', _.size(statuses), ok(statuses)],
                    ['Subscribers', _.size(subscribers), ok(subscribers)],
                    ['Subscriber Patients', _.size(subscriberPatients), ok(subscriberPatients)],
                    ['Transactions', _.size(transactions), ok(transactions)],
                    ['Transmissions', _.size(transmissions), ok(transmissions)],
                ];
                // tslint:disable: no-console
                console.group('loadClaimDetail');
                console.table(report);
                console.groupEnd();
                // tslint:enable: no-console
            }

            this.claimDetails = appendTo<IClaimDetail>(this.claimDetails, claimDetail);

            if (this.features.isDemo || !legacy) {
                const keys: ClaimDetailsKey[] = [
                    'facilities',
                    'providers',
                    'guarantors',
                    'payerSubscribers',
                    'sellers',
                    'subscriberPatients',
                    'chargemasterItems',
                    'payments',
                    'phones',
                    'professionalTemplate',
                    'institutionalTemplate',
                    'nynf3Template',
                    'payers',
                    'subscribers',
                    'eligibilities',
                    'patients',
                    'creditCards',
                    'checkoutCharges',
                    'paymentPlans',
                    'transactions',
                    'invoices',
                    'professionalClaims',
                    'institutionalClaims',
                    'nynf3Claims',
                ];

                keys.forEach(el => this.claimDetailsLoader.set(el, true));
            }
        });
        return claimDetail;
    }

    private getTransactions = (invoices: Invoice[] | undefined): Promise<Transaction[] | undefined> => {
        if (_.isEmpty(invoices)) return Promise.resolve([]);

        const groupIds: string[] = _.compact(_.map(invoices, 'groupId'));

        return this.claimService.fetchTransactionsByGroupIds(groupIds);
    };

    private getPaymentPlans = (claimId: string): Promise<ProviderPaymentPlan[] | undefined> => {
        return this.claimService.fetchPaymentPlansByClaimId(claimId);
    };

    private fetchClaimDetail = async (claim?: ProviderClaim): Promise<IClaimDetail | undefined> => {
        try {
            if (!claim || !claim.id) {
                throw new Error(`No claim provided for fetchClaimDetail`);
            }

            const [
                collections,
                emails,
                documents,
                eligibilities,
                invoices,
                letters,
                pind,
                mail,
                payments,
                receiveFaxes,
                sendFaxes,
                statuses,
                transmissions,
                phones
            ] = await Promise.all([
                this.claimService.fetchCollectionsByClaimId(claim.id),
                this.claimService.fetchEmailsByClaimId(claim.id),
                this.claimService.fetchDocumentsByClaimId(claim.id),
                this.asyncPopulateDetails<ProviderClaimEligibility[]>(
                    this.claimService.fetchEligibilitiesByClaimId(claim.id),
                    el => (this.eligibilities = this.appendTo(this.eligibilities, el)),
                    'eligibilities',
                    this.eligibilityIndex,
                    claim.id,
                ),
                this.asyncPopulateDetails<Invoice[]>(
                    this.claimService.fetchInvoicesByClaimId(claim.id),
                    el => (this.invoices = this.appendTo(this.invoices, el)),
                    'invoices',
                    this.invoiceIndex,
                    claim.id,
                ),
                this.claimService.fetchLettersByClaimId(claim.id),
                this.fetchPIND(claim),
                this.claimService.fetchMailByClaimId(claim.id),
                this.asyncPopulateDetails<ProviderClaimPayment[]>(
                    this.claimService.fetchPaymentsByClaimId(claim.id),
                    el => (this.payments = this.appendTo(this.payments, el)),
                    'payments',
                ),
                this.claimService.fetchReceiveFaxesByClaimId(claim.id),
                this.claimService.fetchSendFaxesByClaimId(claim.id),
                this.claimService.fetchStatusesByClaimId(claim.id),
                this.claimService.fetchTransmissionsById(claim.id),
                this.claimService.fetchPhonesById(claim.id)
            ]);

            let hl7Messages = await this.claimService.fetchHl7ById(claim.id);

            //Get rid of all the JSON payloads
            if(hl7Messages) {
                hl7Messages = hl7Messages.filter(v=>v.type && v.type.toString().indexOf('JSON') === -1);
            }

            //If we have more than 1, just take the first
            if(hl7Messages && hl7Messages.length > 1) {
                hl7Messages = [hl7Messages[0]]
            }

            const guarantorId = pind?.guarantor ? _.get(pind?.guarantor, 'id') : undefined;

            // const [paymentPlans, transactions, checkoutCharges, creditCards] = await Promise.all([
            //     ,
            //     this.asyncPopulateDetails<Transaction[]>(
            //         this.getTransactions(invoices),
            //         el => (this.transactions = this.appendTo(this.transactions, el)),
            //         'transactions',
            //         this.transactionIndex,
            //         claim.id,
            //     ),
            //     ,
            //     this.asyncPopulateDetails<SupportCreditCard[]>(
            //         this.claimService.fetchCheckoutChargesByGuarantorId(guarantorId),
            //         el => (this.checkoutCharges = this.appendTo(this.checkoutCharges, el)),
            //         'checkoutCharges',
            //         this.checkoutChargeIndex,
            //         claim.id,
            //     ),
            //     this.asyncPopulateDetails<SupportCreditCard[]>(
            //         this.claimService.fetchCreditCardsByGuarantorId(guarantorId),
            //         el => (this.creditCards = this.appendTo(this.creditCards, el)),
            //         'creditCards',
            //         this.creditCardIndex,
            //         claim.id,
            //     ),
            // ]);

            return _.assign({}, pind, {
                checkoutCharges: [],
                claim,
                collections,
                emails,
                creditCards: [],
                documents,
                eligibilities,
                hl7Messages,
                invoices,
                letters,
                mail,
                paymentPlans: [],
                payments,
                receiveFaxes,
                sendFaxes,
                statuses,
                transactions: [],
                transmissions,
                phones
            });
        } catch (err) {
            throw err;
        }
    };

    // NB: fetch remaining claim details
    // PIND = Professional, Institutional, NYNF3, Dental
    public fetchPIND = async (claim: ProviderClaim): Promise<IClaimDetail | undefined> => {
        if (claim.type) {
            switch (claim.type) {
                case ClaimType.PROFESSIONAL:
                    return this.fetchProfessionalClaimDetail(claim.id as string);
                case ClaimType.INSTITUTIONAL:
                    return this.fetchInstitutionalClaimDetail(claim.id as string);
                case ClaimType.NYNF3:
                    return this.fetchNynf3ClaimDetail(claim.id as string);
                default:
                    throw new Error(`Unsupported ClaimType: ${claim.type}`);
            }
        }
    };

    public fetchProfessionalClaimDetail = async (claimId: string): Promise<IClaimDetail | undefined> => {
        const professionalClaim = await this.asyncPopulateDetails<ProviderProfessionalClaim>(
            this.claimService.fetchProfessionalClaimByClaimId(claimId),
            el => (this.professionalClaims = this.appendTo(this.professionalClaims, el)),
            'professionalClaims',
        );
        if (professionalClaim) {
            const [professionalTemplate, medicalDetails] = await Promise.all([
                this.asyncPopulateDetails<ProviderProfessionalClaimTemplate>(
                    this.claimService.fetchProfessionalTemplateById(professionalClaim.professionalTemplateId as string),
                    el => (this.professionalTemplates = this.appendTo(this.professionalTemplates, el)),
                    'professionalTemplate',
                    this.templateIndex,
                    claimId,
                    'dsdsds',
                ),
                this.fetchMedicalClaimDetail(professionalClaim),
            ]);
            const chargemasterItemIds = _.compact(_.map(professionalTemplate?.f24ServiceLine, 'chargemasterItemId'));
            const chargemasterItems = await this.asyncPopulateDetails<ProviderChargemasterItem>(
                this.claimService.fetchTemplateChargemasterItems(professionalClaim.chargemasterId, chargemasterItemIds),
                el => (this.chargemasterItems = this.appendTo(this.chargemasterItems, el)),
                'chargemasterItems',
                this.chargemasterItemIndex,
                claimId,
            );
            return _.assign({}, medicalDetails, {
                chargemasterItems,
                professionalClaim,
                professionalTemplate,
            });
        }
        // throw new Error(`No professional claim found for ID: ${claimId}`);
    };

    public fetchInstitutionalClaimDetail = async (claimId: string): Promise<IClaimDetail | undefined> => {
        const institutionalClaim = await this.asyncPopulateDetails<ProviderInstitutionalClaim>(
            this.claimService.fetchInstitutionalClaimByClaimId(claimId),
            el => (this.institutionalClaims = this.appendTo(this.institutionalClaims, el)),
            'institutionalClaims',
        );

        if (institutionalClaim) {
            const [institutionalTemplate, medicalDetails] = await Promise.all([
                this.asyncPopulateDetails<ProviderInstitutionalClaimTemplate>(
                    this.claimService.fetchInstitutionalTemplateById(
                        institutionalClaim.institutionalTemplateId as string,
                    ),
                    el => (this.institutionalTemplates = this.appendTo(this.institutionalTemplates, el)),
                    'institutionalTemplate',
                    this.templateIndex,
                    claimId,
                ),
                this.fetchMedicalClaimDetail(institutionalClaim),
            ]);
            const chargemasterItemIds = _.compact(_.map(institutionalTemplate?.f42ClaimLines, 'chargemasterItemId'));
            const chargemasterItems = await this.asyncPopulateDetails<ProviderChargemasterItem>(
                this.claimService.fetchTemplateChargemasterItems(institutionalClaim.chargemasterId, chargemasterItemIds),
                el => (this.chargemasterItems = this.appendTo(this.chargemasterItems, el)),
                'chargemasterItems',
                this.chargemasterItemIndex,
                claimId,
            );
            return _.assign({}, medicalDetails, {
                chargemasterItems,
                institutionalClaim,
                institutionalTemplate,
            });
        }
        throw new Error(`No institutional claim found for ID: ${claimId}`);
    };

    public fetchNynf3ClaimDetail = async (claimId: string): Promise<IClaimDetail | undefined> => {
        const nynf3Claim = await this.asyncPopulateDetails<ProviderNynf3Claim>(
            this.claimService.fetchNynf3ClaimByClaimId(claimId),
            el => (this.nynf3Claims = this.appendTo(this.nynf3Claims, el)),
            'nynf3Claims',
        );

        if (nynf3Claim) {
            const [nynf3Template, medicalDetails] = await Promise.all([
                this.asyncPopulateDetails<ProviderNynf3ClaimTemplate>(
                    this.claimService.fetchNynf3TemplateById(nynf3Claim.nynf3TemplateId as string),
                    el => (this.nynf3Templates = this.appendTo(this.nynf3Templates, el)),
                    'nynf3Template',
                    this.templateIndex,
                    claimId,
                ),
                this.fetchMedicalClaimDetail(nynf3Claim),
            ]);
            const chargemasterItemIds = _.compact(_.map(nynf3Template?.f15ServiceLines, 'chargemasterItemId'));
            const chargemasterItems = this.claimService.fetchTemplateChargemasterItems(
                nynf3Claim.chargemasterId,
                chargemasterItemIds,
            );
            return _.assign({}, medicalDetails, {
                chargemasterItems,
                nynf3Claim,
                nynf3Template,
            });
        }
        throw new Error(`No nynf3 claim found for ID: ${claimId}`);
    };

    @action
    private appendTo<T>(array: T[], model?: T | T[], key?: string): T[] {
        if (model) {
            return _.unionBy(array, _.concat([], model), key || 'id');
        } else {
            return array;
        }
    }

    @action
    private asyncPopulateDetails = async <D>(
        // TODO: fix to the type
        promise: Promise<any>,
        mutateArr: (el: any[]) => void,
        detailsKey: ClaimDetailsKey | ClaimDetailsKey[],
        indexMap?: Map<string, string[]>,
        indexKey?: string | undefined,
        arrKey?: string,
    ) => {
        if (Array.isArray(detailsKey)) {
            detailsKey.forEach(key => this.claimDetailsLoader.set(key, false));
        } else {
            this.claimDetailsLoader.set(detailsKey, false);
        }
        const el = await promise;

        runInAction(() => {
            if (Array.isArray(detailsKey)) {
                detailsKey.forEach(key => this.claimDetailsLoader.set(key, true));
            } else {
                this.claimDetailsLoader.set(detailsKey, true);
            }

            if (el) {
                mutateArr(el);
            }

            indexKey && indexMap && this.updateIndex(indexMap, indexKey, el);
        });

        return el as D;
    };

    @action
    public fetchMedicalClaimDetail = async (medicalClaim: ProviderMedicalClaim): Promise<IClaimDetail | undefined> => {
        if (!medicalClaim) {
            return;
        }

        const [facility, guarantor, payerSubscribers, providers, seller, subscriberPatients] = await Promise.all([
            this.asyncPopulateDetails<ProviderFacility>(
                this.claimService.fetchFacilityById(medicalClaim.facilityId),
                el => (this.facilities = this.appendTo(this.facilities, el)),
                'facilities',
                this.facilityIndex,
                medicalClaim.claimId,
            ),
            this.asyncPopulateDetails<ProviderGuarantor>(
                this.claimService.fetchGuarantorById(medicalClaim.guarantorId),
                el => (this.guarantors = this.appendTo(this.guarantors, el)),
                'guarantors',
                this.guarantorIndex,
                medicalClaim.claimId,
            ),
            this.asyncPopulateDetails<ProviderPayerSubscriber[]>(
                this.claimService.fetchPayerSubscribersById(medicalClaim.payerSubscriberIds),
                el => (this.payerSubscribers = this.appendTo(this.payerSubscribers, el)),
                ['payerSubscribers', 'payers'],
                this.payerSubscriberIndex,
                medicalClaim.claimId,
                'payerSubscribers',
            ),
            this.asyncPopulateDetails<ProviderProvider[]>(
                this.claimService.fetchProvidersById(medicalClaim.providerIds),
                el => (this.providers = this.appendTo(this.providers, el)),
                'providers',
                this.providerIndex,
                medicalClaim.claimId,
            ),
            this.asyncPopulateDetails<ProviderSeller>(
                this.claimService.fetchSellerById(medicalClaim.sellerId),
                el => (this.sellers = this.appendTo(this.sellers, el)),
                'sellers',
                this.sellerIndex,
                medicalClaim.claimId,
            ),
            this.asyncPopulateDetails<ProviderSubscriberPatient[]>(
                this.claimService.fetchSubscriberPatientsById(medicalClaim.subscriberPatientIds),
                el => (this.subscriberPatients = this.appendTo(this.subscriberPatients, el)),
                ['subscriberPatients', 'patients', 'subscribers'],
                this.subscriberPatientIndex,
                medicalClaim.claimId,
            ),
        ]);

        return {
            facility,
            guarantor,
            patients: _.flatMap(subscriberPatients, 'patient'),
            payers: _.flatMap(payerSubscribers, 'payer'),
            payerSubscribers,
            providers,
            seller,
            subscribers: _.flatMap(subscriberPatients, 'subscriber'),
            subscriberPatients,
        };
    };

    @action public async queryPatients(query: string): Promise<ProviderPatient[] | undefined> {
        if (this.features.isDemo) {
            return this.claimGenerator.queryPatients(query);
        } else {
            return ClaimService.queryPatients(query);
        }
    }

    @action public async querySellers(query: string): Promise<ProviderSeller[] | undefined> {
        if (this.features.isDemo) {
            return this.claimGenerator.querySellers(query);
        } else {
            return ClaimService.querySellers(query);
        }
    }

    @action public async queryPayers(query: string): Promise<ProviderPayer[] | undefined> {
        if (this.features.isDemo) {
            return this.claimGenerator.queryPayers(query);
        } else {
            return ClaimService.queryPayers(query);
        }
    }

    @action public async queryProviders(query: string): Promise<ProviderProvider[] | undefined> {
        if (this.features.isDemo) {
            return this.claimGenerator.queryProviders(query);
        } else {
            return ClaimService.queryProviders(query);
        }
    }

    @action public async queryGuarantors(query: string): Promise<ProviderGuarantor[] | undefined> {
        if (this.features.isDemo) {
            return this.claimGenerator.queryGuarantors(query);
        } else {
            return ClaimService.queryGuarantors(query);
        }
    }

    @action public async queryFacilities(query: string): Promise<ProviderFacility[] | undefined> {
        if (this.features.isDemo) {
            return this.claimGenerator.queryFacilities(query);
        } else {
            return ClaimService.queryFacilities(query);
        }
    }

    @action public async querySubscribers(query: string): Promise<ProviderSubscriber[] | undefined> {
        if (this.features.isDemo) {
            return this.claimGenerator.querySubscribers(query);
        } else {
            return ClaimService.querySubscribers(query);
        }
    }

    @action public async loadBasicClaimSearchResults(term: string): Promise<ProviderClaim[]> {
        const claims: ProviderClaim[] = await (this.features.isDemo
            ? this.claimGenerator.generateBasicSearchResults(term)
            : this.claimService.fetchBasicSearchResults(term));
        runInAction(() => {
            _.forEach(this.claims, claim => updateClaimStatus(claim));
            this.claims = appendTo<ProviderClaim>(this.claims, claims);
        });
        return claims;
    }

    @action public async oldLoadBasicClaimSearchResults(term: string): Promise<ProviderClaim[]> {
        const claims: ProviderClaim[] = await (this.features.isDemo
            ? this.claimGenerator.generateBasicSearchResults(term)
            : this.claimService.oldFetchBasicSearchResults(term));
        runInAction(() => {
            _.forEach(this.claims, claim => updateClaimStatus(claim));
            this.claims = appendTo<ProviderClaim>(this.claims, claims);
        });
        return claims;
    }

    @action
    public async loadMailListing(ilr: IItemListingRequest): Promise<IItemListing<ProviderMail>> {
        const lpgr = ilr2lptr(ilr);
        const listing = await (this.features.isDemo
            ? this.claimGenerator.loadMailListing(lpgr)
            : this.claimService.fetchMailListing(lpgr));
        if (!listing) {
            throw new Error(`No mails available`);
        }
        runInAction(() => {
            this.mails = appendTo<ProviderMail>(this.mails, listing.items);
        });
        return listing;
    }


    @action
    public async loadPhoneListing(ilr: IItemListingRequest): Promise<IItemListing<ProviderPhone>> {
        const base = parseItemListingRequest(ilr) as ListProviderPhoneRequest;
        const filters = createRequestFilters(ilr.filters, true);

        const listing = await (this.features.isDemo
            ? (Promise.resolve([] as any) as Promise<IItemListing<ProviderPhone> | undefined>)
            : this.claimService.fetchPhones({ ...base, ...filters }));
        if (!listing) {
            throw new Error(`No phone calls available`);
        }
        runInAction(() => {
            this.phones = appendTo<ProviderPhone>(this.phones, listing.items);
        });
        return listing;
    }

    @action
    public async loadReceiveFaxListing(ilr: IItemListingRequest): Promise<IItemListing<ProviderReceiveFax>> {
        const base = parseItemListingRequest(ilr) as ListProviderReceiveFaxesRequest;
        const filters = createRequestFilters(ilr.filters, true);

        const listing = await (this.features.isDemo
            ? this.claimGenerator.loadReceiveFaxListing({ ...base, ...filters })
            : this.claimService.fetchReceiveFaxes({ ...base, ...filters }));
        if (!listing) {
            throw new Error(`No faxes available`);
        }
        runInAction(() => {
            this.receiveFaxes = appendTo<ProviderReceiveFax>(this.receiveFaxes, listing.items);
        });
        return listing;
    }

    @action
    public async loadSendFaxListing(ilr: IItemListingRequest): Promise<IItemListing<ProviderSendFax>> {
        const base = parseItemListingRequest(ilr) as ListProviderSendFaxesRequest;
        const filters = createRequestFilters(ilr.filters, true);

        const listing = await (this.features.isDemo
            ? this.claimGenerator.loadSendFaxListing({ ...base, ...filters })
            : this.claimService.fetchSendFaxes({ ...base, ...filters }));
        if (!listing) {
            throw new Error(`No faxes available`);
        }
        runInAction(() => {
            this.sendFaxes = appendTo<ProviderSendFax>(this.sendFaxes, listing.items);
        });
        return listing;
    }

    @action
    public async loadCommunicationListing(ilr: IItemListingRequest): Promise<IItemListing<ICommunication>> {
        const lpgr = ilr2lpcmr(ilr);
        const listing = await (this.features.isDemo
            ? this.claimGenerator.loadCommunicationListing(lpgr)
            : this.claimService.fetchCommunicationListing(lpgr));
        if (!listing) {
            throw new Error(`No communication available`);
        }
        runInAction(() => {
            this.mails = appendTo<ICommunication>(this.communications, listing.items);
        });
        return listing;
    }

    @action
    public async loadLetterListing(ilr: IItemListingRequest): Promise<IItemListing<ProviderLetter>> {
        const lpgr = ilr2lplr(ilr);
        const listing = await (this.features.isDemo
            ? this.claimGenerator.loadLetterListing(lpgr)
            : this.claimService.fetchLetterListing(lpgr));
        if (!listing) {
            throw new Error(`No letters available`);
        }
        runInAction(() => {
            this.mails = appendTo<ProviderMail>(this.mails, listing.items);
        });
        return listing;
    }

    @action
    public async loadCartListing(ilr: IItemListingRequest): Promise<IItemListing<ProviderMailCart>> {
        const base = parseItemListingRequest(ilr) as ListProviderMailCartsRequest;
        const filters = createRequestFilters(ilr.filters, true);

        const listing = await (this.features.isDemo
            ? this.claimGenerator.loadCartListing({ ...base, ...filters })
            : this.claimService.fetchCartListing({ ...base, ...filters }));
        if (!listing) {
            throw new Error(`No carts available`);
        }
        runInAction(() => {
            this.mailCarts = appendTo<ProviderMailCart>(this.mailCarts, listing.items);
        });
        return listing;
    }

    @action
    public async addInvoiceLineItem(invoiceId: string, name: string, amount: number): Promise<void> {
        const amt = { amount: String(amount), currency: CurrencyType.USD };
        const invoice = await (this.features.isDemo
            ? this.claimGenerator.generateInvoiceLineItem(invoiceId, name, amt)
            : this.claimService.addInvoiceLineItem(invoiceId, name, amt));
        runInAction(() => {
            if (invoice) {
                const culled = _.reject(this.invoices, { id: invoice.id });
                this.invoices = _.concat(culled, invoice);
            }
        });
    }

    @action
    public async createCollectionPayment(
        providerCreateCollectionPaymentPayload: ProviderCreateCollectionPaymentPayload,
    ): Promise<void> {
        const collection = await (this.features.isDemo
            ? this.claimGenerator.createCollectionPayment(providerCreateCollectionPaymentPayload)
            : this.claimService.createCollectionPayment(providerCreateCollectionPaymentPayload));

        runInAction(() => {
            if (collection) {
                this.collections = appendTo<ProviderClaim>(this.collections, collection);
            }
        });
    }

    @action
    public async updateClaimMaxGuarantorResponsibility(
        claimGuarantorResponsibility: ClaimGuarantorResponsibility,
        claimId: string
    ): Promise<void> {
        const resp = await (this.features.isDemo
        ? this.claimGenerator.updateClaimGuarantorResponsibility(claimGuarantorResponsibility, claimId) :
            this.claimService.updateClaimGuarantorResponsibility(claimGuarantorResponsibility, claimId)
        );
    }

    @action
    public async addInvoicePaymentPlan(
        invoiceId: string,
        amountPerCycle: number,
        intervalCount: number,
        intervalSpan: PaymentPlanIntervalType,
    ): Promise<void> {
        const paymentPlan = await (this.features.isDemo
            ? this.claimGenerator.generateInvoicePaymentPlan(invoiceId, amountPerCycle, intervalCount, intervalSpan)
            : this.claimService.addInvoicePaymentPlan(invoiceId, amountPerCycle, intervalCount, intervalSpan));
        runInAction(() => {
            if (paymentPlan) {
                this.paymentPlans = appendTo<ProviderClaim>(this.paymentPlans, paymentPlan);
            }
        });
    }

    @action
    public async disablePaymentPlan(paymentPlanId: string): Promise<void> {
        await (this.features.isDemo
            ? this.claimGenerator.disableInvoicePaymentPlan(paymentPlanId)
            : this.claimService.disableInvoicePaymentPlan(paymentPlanId));
        runInAction(() => {
            const paymentPlan = _.find(this.paymentPlans, { id: paymentPlanId });
            this.paymentPlans = _.compact(_.without(this.paymentPlans, paymentPlan));
        });
    }

    @action
    public async getGuarantorCheckoutUrl(guarantorId: string): Promise<SupportShortUrl | undefined> {
        // NB: no local caching of URLs
        return this.features.isDemo
            ? this.claimGenerator.generateGuarantorCheckoutUrl(guarantorId)
            : this.claimService.fetchGuarantorCheckoutUrl(guarantorId);
    }

    @action
    public async getGuarantorClaims(guarantorId: string): Promise<ProviderClaim[] | undefined> {
        // NB: no local caching of URLs
        return this.features.isDemo
            ? this.claimGenerator.generateGuarantorClaims(guarantorId)
            : this.claimService.fetchGuarantorClaims(guarantorId);
    }

    @action
    public async getGuarantorViewBillUrl(guarantorId: string): Promise<TemporaryDocumentUrl | undefined> {
        // NB: no local caching of URLs
        return this.features.isDemo
            ? this.claimGenerator.generateGuarantorViewBillUrl(guarantorId)
            : this.claimService.fetchGuarantorViewBillUrl(guarantorId);
    }

    @action
    public async getGuarantorBillingDocuments(
        claimId: string,
        guarantorId: string,
    ): Promise<ProviderDocument[] | undefined> {
        return this.features.isDemo
            ? this.claimGenerator.loadGuarantorBillingDocuments(claimId, guarantorId)
            : this.claimService.fetchGuarantorBillingDocuments(claimId, guarantorId);
    }

    @action
    public async loadFacilityById(facilityId: string): Promise<ProviderFacility | undefined> {
        return this.features.isDemo
            ? this.claimGenerator.loadFacilityById(facilityId)
            : this.claimService.fetchFacilityById(facilityId);
    }

    @action
    public async loadGuarantorById(guarantorId: string): Promise<ProviderGuarantor | undefined> {
        return this.features.isDemo
            ? this.claimGenerator.loadGuarantorById(guarantorId)
            : this.claimService.fetchGuarantorById(guarantorId);
    }

    @action
    public async loadPatientById(patientId: string): Promise<ProviderPatient | undefined> {
        return this.features.isDemo
            ? this.claimGenerator.loadPatientById(patientId)
            : this.claimService.fetchPatientById(patientId);
    }

    @action
    public async loadPayerById(payerId: string): Promise<ProviderPayer | undefined> {
        return this.features.isDemo
            ? this.claimGenerator.loadPayerById(payerId)
            : this.claimService.fetchPayerById(payerId);
    }

    @action
    public async loadClaimBundle(bundleId: string): Promise<ProviderClaim[]> {
        return this.features.isDemo
            ? this.claimGenerator.loadClaimBundle(bundleId)
            : this.claimService.fetchClaimBundle(bundleId);
    }

    @action
    public async loadEmails(claimId: string): Promise<ProviderSendEmail[]| undefined> {
        const demoEmails: ProviderSendEmail[] = [];

        return this.features.isDemo
            ? demoEmails : this.claimService.fetchEmailsByClaimId(claimId);
    }

    @action
    public async loadProviderById(providerId: string): Promise<ProviderProvider | undefined> {
        return this.features.isDemo
            ? this.claimGenerator.loadProviderById(providerId)
            : this.claimService.fetchProviderById(providerId);
    }
    @action
    public async loadProvidersById(providerIds: string[]): Promise<ProviderProvider[] | undefined> {
        return this.features.isDemo
            ? this.claimGenerator.loadProvidersById(providerIds)
            : this.claimService.fetchProvidersById(providerIds);
    }

    @action
    public async loadSellerById(sellerId: string): Promise<ProviderSeller | undefined> {
        return this.features.isDemo
            ? this.claimGenerator.loadSellerById(sellerId)
            : this.claimService.fetchSellerById(sellerId);
    }

    @action
    public async loadSubscriberById(subscriberId: string): Promise<ProviderSubscriber | undefined> {
        return this.features.isDemo
            ? this.claimGenerator.loadSubscriberById(subscriberId)
            : this.claimService.fetchSubscriberById(subscriberId);
    }

    @action
    public async viewLetterById(letterId: string): Promise<TemporaryDocumentUrl | undefined> {
        return this.features.isDemo
            ? this.claimGenerator.viewLetterById(letterId)
            : this.claimService.viewLetterById(letterId);
    }

    @action
    public async viewFaxById(faxId: string): Promise<TemporaryDocumentUrl | undefined> {
        return this.features.isDemo ? this.claimGenerator.viewFaxById(faxId) : this.claimService.viewFaxById(faxId);
    }

    @action
    public async loadDocument(
        documentId: string,
        withThumbnail: boolean = false,
    ): Promise<ProviderDocument | undefined> {
        const key = buildCacheKey({ documentId });
        let document = this.documentQueries.get(key);
        if (!document) {
            document = await (this.features.isDemo
                ? this.claimGenerator.loadDocumentById(documentId)
                : this.claimService.fetchDocumentById(documentId));
            this.documentQueries.set(key, document as ProviderDocument);
        }

        if (withThumbnail && document?.id && !document.thumbnail) {
            await ClaimService.refreshDocumentMetadata(document.id);

            document = await (this.features.isDemo
                ? this.claimGenerator.loadDocumentById(documentId)
                : this.claimService.fetchDocumentById(documentId));
            this.documentQueries.set(key, document as ProviderDocument);
        }

        runInAction(() => {
            this.documents = appendTo<ProviderDocument>(this.documents, document);
        });
        return document;
    }

    @action
    public async loadDocumentDownloadUrl(
        documentId: string,
        documentType?: ProviderDisplayDocumentType,
    ): Promise<ProviderDocumentUrl | undefined> {
        const key = buildCacheKey({ documentId });
        let documentUrl = this.documentUrlQueries.get(key);
        if (!documentUrl) {
            documentUrl = await (this.features.isDemo
                ? this.claimGenerator.loadDocumentUrlByDocumentId(documentId, documentType)
                : this.claimService.fetchDocumentUrlByDocumentId(documentId));
            this.documentUrlQueries.set(key, documentUrl as ProviderDocumentUrl);
        }
        runInAction(() => {
            this.documentUrls = appendTo<ProviderDocumentUrl>(this.documentUrls, documentUrl);
        });
        return documentUrl;
    }

    @action
    public async getDocumentTranscript(
        documentId: string
    ): Promise<ProviderDocumentTranscription | undefined> {
        return this.claimService.fetchDocumentTranscriptByDocumentId(documentId);
    }

    @action
    public async loadPaymentDownloadUrl(paymentId: string): Promise<ProviderDocumentUrl | undefined> {
        const key = buildCacheKey({ paymentId });
        let documentUrl = this.documentUrlQueries.get(key);
        if (!documentUrl) {
            documentUrl = await (this.features.isDemo
                ? this.claimGenerator.fetchDocumentUrlByPaymentId(paymentId)
                : this.claimService.fetchDocumentUrlByPaymentId(paymentId));
            this.documentUrlQueries.set(key, documentUrl as ProviderDocumentUrl);
        }
        runInAction(() => {
            this.documentUrls = appendTo<ProviderDocumentUrl>(this.documentUrls, documentUrl);
        });
        return documentUrl;
    }

    @action
    public async loadMailCart(documentId: string): Promise<ProviderMailCart | undefined> {
        const key = buildCacheKey({ documentId });
        let mailCart = this.mailCartQueries.get(key);
        if (!mailCart) {
            mailCart = await (this.features.isDemo
                ? this.claimGenerator.loadMailCartById(documentId)
                : this.claimService.fetchMailCartById(documentId));
            this.mailCartQueries.set(key, mailCart as ProviderMailCart);
        }
        runInAction(() => {
            this.mailCarts = appendTo<ProviderMailCart>(this.mailCarts, mailCart);
        });
        return mailCart;
    }

    @action
    public async refundCheckoutCharge(
        checkoutCharge: SupportCheckoutCharge,
        supportRefundCollectionPaymentPayload: SupportRefundCollectionPaymentPayload,
    ): Promise<void> {
        const updatedCharge = await (this.features.isDemo
            ? this.claimGenerator.refundCheckoutCharge(checkoutCharge)
            : this.claimService.refundCheckoutCharge(checkoutCharge, supportRefundCollectionPaymentPayload));
        runInAction(() => {
            if (updatedCharge) {
                const idx = _.findIndex(this.checkoutCharges, { id: updatedCharge.id });
                this.checkoutCharges = _.set(this.checkoutCharges, idx, updatedCharge);
            }
        });
    }

    @action
    public async refundCollectionPayment(
        paymentId: string,
        supportRefundCollectionPaymentPayload: SupportRefundCollectionPaymentPayload,
    ): Promise<void> {
        const refundedPayment = await (this.features.isDemo
            ? this.claimGenerator.refundCollectionPayment(paymentId, supportRefundCollectionPaymentPayload)
            : this.claimService.refundCollectionPayment(paymentId, supportRefundCollectionPaymentPayload));
        runInAction(() => {
            if (!refundedPayment) {
                return;
            }

            this.collections = this.collections.map(collection => {
                return collection.id === refundedPayment?.id ? refundedPayment : collection;
            });
        });
    }

    @action
    public async getBillingProviders(): Promise<ProviderProvider[] | undefined> {
        const providers = await (this.features.isDemo
            ? this.claimGenerator.loadBillingProviders()
            : this.claimService.fetchBillingProviders());
        runInAction(() => {
            this.providers = appendTo<ProviderProvider>(this.providers, providers);
        });
        return providers;
    }

    public findClaim = (claimId: string): ProviderClaim | undefined => {
        return _.find(this.claims, { id: claimId });
    };
    public findClaimDetail = (claimId: string): IClaimDetail | undefined => {
        return _.find(this.claimDetails, { claim: { id: claimId } });
    };
    public findProfessionalClaim = (claimId: string): ProviderProfessionalClaim | undefined => {
        return _.find(this.professionalClaims, { claimId });
    };
    public findInstitutionalClaim = (claimId: string): ProviderInstitutionalClaim | undefined => {
        return _.find(this.institutionalClaims, { claimId });
    };
    public findNynf3Claim = (claimId: string): ProviderNynf3Claim | undefined => {
        return _.find(this.nynf3Claims, { claimId });
    };
    public findStatuses = (claimId: string): ProviderStatus[] | undefined => {
        // return _.filter(this.statuses, { claimId });
        return;
    };
    public findDocuments = (claimId: string): ProviderDocument[] | undefined => {
        return _.filter(this.documents, { claimId });
    };
    public findPayerSubscribers = (claimId: string): ProviderPayerSubscriber[] | undefined => {
        const claim = this.findClaim(claimId);
        if (claim) {
            let ids: string[] | undefined;
            switch (claim.type) {
                case ClaimType.PROFESSIONAL:
                    ids = _.get(this.findProfessionalClaim(claimId), 'payerSubscriberIds', undefined);
                    break;
                case ClaimType.INSTITUTIONAL:
                    ids = _.get(this.findInstitutionalClaim(claimId), 'payerSubscriberIds', undefined);
                    break;
                case ClaimType.NYNF3:
                    ids = _.get(this.findNynf3Claim(claimId), 'payerSubscriberIds', undefined);
                    break;
                default:
                    ids = undefined;
            }
            return this.payerSubscribers.filter(
                (ps: ProviderPayerSubscriber): boolean => !!ids && ids.includes(ps.id as string),
            );
        }
    };
    public findPayerSubscriber = (payerSubscriberId: string): ProviderPayerSubscriber | undefined => {
        return _.find(this.payerSubscribers, { id: payerSubscriberId });
    };
    public findSubscriberPatients = (claimId: string): ProviderSubscriberPatient[] | undefined => {
        const claim = this.findClaim(claimId);
        if (claim) {
            let ids: string[] | undefined;
            switch (claim.type) {
                case ClaimType.PROFESSIONAL:
                    ids = _.get(this.findProfessionalClaim(claimId), 'subscriberPatientIds', undefined);
                    break;
                case ClaimType.INSTITUTIONAL:
                    ids = _.get(this.findInstitutionalClaim(claimId), 'subscriberPatientIds', undefined);
                    break;
                case ClaimType.NYNF3:
                    ids = _.get(this.findNynf3Claim(claimId), 'subscriberPatientIds', undefined);
                    break;
                default:
                    ids = undefined;
            }
            return this.subscriberPatients.filter(
                (sp: ProviderSubscriberPatient): boolean => !!ids && ids.includes(sp.id as string),
            );
        }
    };
    public findCollections = (claim?: ProviderClaim): ProviderCollectionPayment[] | undefined => {
        if (claim && claim.id) {
            return _.filter(this.collections, { claimId: claim.id });
        }
    };
    public findPayments = (claim?: ProviderClaim): ProviderClaimPayment[] | undefined => {
        if (claim && claim.id) {
            const claimId = claim.id as string;
            return this.payments.filter((payment: ProviderClaimPayment): boolean => {
                switch (claim.type) {
                    case ClaimType.INSTITUTIONAL:
                        return payment.institutionalId === _.get(this.findInstitutionalClaim(claimId), 'id');
                    case ClaimType.PROFESSIONAL:
                        return payment.professionalId === _.get(this.findProfessionalClaim(claimId), 'id');
                    case ClaimType.NYNF3:
                        return payment.nynf3Id === _.get(this.findNynf3Claim(claimId), 'id');
                    default:
                        return false;
                }
            });
        }
    };

    // NB: some models require lookup through an index, as claimId is not on the model
    public findEligibilities = (claimId: string): ProviderClaimEligibility[] | undefined => {
        const ids = this.eligibilityIndex.get(claimId);
        return _.filter(this.eligibilities, eligibility => _.includes(ids, eligibility.id));
    };
    public findFacilities = (claimId: string): ProviderFacility[] | undefined => {
        const ids = this.facilityIndex.get(claimId);
        return _.filter(this.facilities, facility => _.includes(ids, facility.id));
    };
    public findGuarantors = (claimId: string): ProviderGuarantor[] | undefined => {
        const ids = this.guarantorIndex.get(claimId);
        return _.filter(this.guarantors, guarantor => _.includes(ids, guarantor.id));
    };
    public findHL7Messages = (claimId: string): SupportHL7Message[] | undefined => {
        const ids = this.hl7MessageIndex.get(claimId);
        return _.filter(this.hl7Messages, hl7Message => _.includes(ids, hl7Message.id));
    };
    public findInvoices = (claimId: string): Invoice[] | undefined => {
        const ids = this.invoiceIndex.get(claimId);
        return _.filter(this.invoices, invoice => _.includes(ids, invoice.id));
    };
    public findLetters = (claimId: string): ProviderLetter[] | undefined => {
        const ids = this.letterIndex.get(claimId);
        return _.filter(this.letters, letter => _.includes(ids, letter.id));
    };
    public findMails = (claimId: string): ProviderMail[] | undefined => {
        const ids = this.mailIndex.get(claimId);
        return _.filter(this.mails, m => _.includes(ids, m.id));
    };
    public findPhones = (claimId: string): ProviderPhone[] | undefined => {
        const ids = this.phoneIndex.get(claimId);
        return _.filter(this.phones, p=> _.includes(ids, p.id));
    };

    public findProviders = (claimId: string): ProviderProvider[] | undefined => {
        const ids = this.providerIndex.get(claimId);
        return _.filter(this.providers, p => _.includes(ids, p.id));
    };
    public findReceiveFaxes = (claimId: string): ProviderReceiveFax[] | undefined => {
        const ids = this.receiveFaxIndex.get(claimId);
        return _.filter(this.receiveFaxes, receiveFax => _.includes(ids, receiveFax.id));
    };
    public findSeller = (claimId: string): ProviderSeller[] | undefined => {
        const ids = this.sellerIndex.get(claimId);
        return _.filter(this.sellers, seller => _.includes(ids, seller.id));
    };
    public findSendFaxes = (claimId: string): ProviderSendFax[] | undefined => {
        const ids = this.sendFaxIndex.get(claimId);
        return _.filter(this.sendFaxes, sendFax => _.includes(ids, sendFax.id));
    };
    public findTransactions = (claimId: string): Transaction[] | undefined => {
        const ids = this.transactionIndex.get(claimId);
        return _.filter(this.transactions, transaction => _.includes(ids, transaction.id));
    };
    public findTransmissions = (claimId: string): ProviderClaimTransmission[] | undefined => {
        const ids = this.transmissionIndex.get(claimId);
        const filteredTransmissions = _.filter(this.transmissions, transmission => _.includes(ids, transmission.id));

        // Manually replace transmission statuses
        const statusDictionary = { ['NCCI']: 'WARNING', ['ERA Denial']: 'DENIED' };

        return _.map(filteredTransmissions, transmission => {
            return {
                ...transmission,
                statuses: _.map(transmission.statuses, status => {
                    let newStatus = status.status;
                    for (const key in statusDictionary) {
                        if (_.toLower(status.messages).includes(_.toLower(key))) {
                            newStatus = statusDictionary[key];
                        }
                    }

                    return { ...status, status: newStatus };
                }),
            };
        });
    };
    public findInstitutionalTemplate = (claimId: string): ProviderInstitutionalClaimTemplate | undefined => {
        const ids = this.templateIndex.get(claimId);
        return _.find(this.institutionalTemplates, template => _.includes(ids, template.id));
    };
    public findEmails = (claimId: string): ProviderSendEmail[] | undefined => {
        const ids = this.emailIndex.get(claimId);
        return _.filter(this.emails, email => _.includes(ids, email.id));
    }
    public findNynf3Template = (claimId: string): ProviderNynf3ClaimTemplate | undefined => {
        const ids = this.templateIndex.get(claimId);
        return _.find(this.nynf3Templates, template => _.includes(ids, template.id));
    };
    public findPayerSubscribersByClaim = (claimId: string): ProviderPayerSubscriber[] | undefined => {
        const ids = this.payerSubscriberIndex.get(claimId);
        return _.filter(this.payerSubscribers, el => _.includes(ids, el.id));
    };
    public findSubscriberPatientsByClaim = (claimId: string): ProviderSubscriberPatient[] | undefined => {
        const ids = this.subscriberPatientIndex.get(claimId);
        return _.filter(this.subscriberPatients, el => _.includes(ids, el.id));
    };

    public findProfessionalTemplate = (claimId: string): ProviderProfessionalClaimTemplate | undefined => {
        const ids = this.templateIndex.get(claimId);
        return _.find(this.professionalTemplates, template => _.includes(ids, template.id));
    };

    public findChargemasterItems = (claimId: string): ProviderChargemasterItem[] | undefined => {
        const ids = this.chargemasterItemIndex.get(claimId);
        return _.filter(this.chargemasterItems, chargemasterItem => _.includes(ids, chargemasterItem.id));
    };
    public findCheckoutCharges = (guarantor: ProviderGuarantor | undefined): SupportCheckoutCharge[] | undefined => {
        const guarantorId = _.get(guarantor, 'id', '');
        const ids = this.checkoutChargeIndex.get(guarantorId);
        return _.filter(this.checkoutCharges, checkoutCharge => _.includes(ids, checkoutCharge.id));
    };

    public findCreditCards = (guarantor: ProviderGuarantor | undefined): SupportCreditCard[] | undefined => {
        const guarantorId = _.get(guarantor, 'id', '');
        const ids = this.creditCardIndex.get(guarantorId);
        return _.filter(this.creditCards, creditCard => _.includes(ids, creditCard.id));
    };
    public findPaymentPlansByGuarantorId = (guarantorId: string): ProviderPaymentPlan[] | undefined => {
        return _.filter(this.paymentPlans, { guarantorId });
    };
    public findPaymentPlansByInvoiceId = (invoiceId: string): ProviderPaymentPlan[] | undefined => {
        return _.filter(this.paymentPlans, { invoiceId });
    };

    @action
    private updateIndex(map: Map<string, string[]>, key: string, values?: { id?: string }[] | { id?: string }): void {
        if (values) {
            const arrValues = Array.isArray(values) ? values : [values];
            const newValues: string[] = _.compact(_.map(arrValues, 'id'));
            const oldValues: string[] = map.get(key) || [];
            map.set(key, _.concat(oldValues, newValues));
            if (_.some(newValues, { id: 'id' })) {
                // tslint:disable-next-line: no-console
                console.warn(`Claim store index contains test data: ${values}`);
            }
        }
    }
}

// Convert from IItemListingRequest to ListProviderClaimsRequest
export const ilr2lpcr = (ilr: IItemListingRequest): ListProviderClaimsRequest => {
    const base = parseItemListingRequest(ilr) as ListProviderClaimsRequest;
    base.filterClaimType =  ilr.institutional ? ListProviderClaimsFilterClaimTypeEnum.INSTITUTIONAL : ListProviderClaimsFilterClaimTypeEnum.PROFESSIONAL;
    const filters = createRequestFilters(ilr.filters, true);
    return {
        ...base,
        ...filters,
    };
};

// Convert from IItemListingRequest to ListProviderMailRequest
export const ilr2lptr = (ilr: IItemListingRequest): ListProviderMailRequest => {
    const base = parseItemListingRequest(ilr) as ListProviderMailRequest;
    const filters = createRequestFilters(ilr.filters, true);
    return {
        ...base,
        ...filters,
    };
};

// Convert from IItemListingRequest to ListProviderCommunicationsRequest
export const ilr2lpcmr = (ilr: IItemListingRequest): ListProviderCommunicationsRequest => {
    const base = parseItemListingRequest(ilr) as ListProviderCommunicationsRequest;
    const filters = createRequestFilters(ilr.filters, true);
    return {
        ...base,
        ...filters,
    };
};

// Convert from IItemListingRequest to ListProviderLettersRequest
export const ilr2lplr = (ilr: IItemListingRequest): ListProviderLettersRequest => {
    const base = parseItemListingRequest(ilr) as ListProviderLettersRequest;
    const filters = createRequestFilters(ilr.filters, true);
    return {
        ...base,
        ...filters,
    };
};
